import { useAutoContextInit } from '@/hooks/useAutoContextInit';
import { useChatApi } from '@/hooks/useChatApi';
import { useContextSuggestions } from '@/hooks/useContextSuggestions';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { AnimatePresence, motion } from 'framer-motion';
import { memo } from 'react';
import { Button } from './ui/button';

const ContextSuggestion = () => {
  const { isOpen, setOpen } = useChatStore();
  const { sendSuggestion, sendQuickAnswer } = useChatApi();
  const { currentSuggestion } = useContextSuggestions();
  const { config } = useConfigStore();
  const generalSpecificVariant = config.ui?.generalSpecificVariant;

  // Auto-initialize context suggestions system
  useAutoContextInit();

  const handleClick = () => {
    if (!currentSuggestion) return;
    // Just open the chat if there is no chat question
    setOpen(true);

    if (!currentSuggestion.chatQuestion) return;

    // Check if this is a quick answer or regular suggestion
    if (currentSuggestion.quickAnswer) {
      // Send quick answer without API call
      sendQuickAnswer(currentSuggestion.chatQuestion, currentSuggestion.quickAnswer);
    } else {
      // Send regular suggestion to API
      sendSuggestion(currentSuggestion.chatQuestion);
    }
  };

  return (
    <div
      className={cn(
        'pointer-events-auto absolute bottom-[75px] right-[20px] z-30 flex items-center justify-end opacity-100 transition-[opacity,transform] delay-[0.4s,0s] duration-200',
        isOpen && 'pointer-events-none opacity-0 delay-0',
        {
          'pointer-events-none': !currentSuggestion?.text,
          'bottom-[88px]': generalSpecificVariant === 'velio',
        }
      )}
    >
      <AnimatePresence mode="wait">
        {currentSuggestion && (
          <motion.div
            key={currentSuggestion.text}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.2 }}
            className="max-w-[calc(100vw-40px)]"
          >
            <Button
              variant={'suggestion'}
              size={'md'}
              onClick={handleClick}
              className="inline-block h-auto max-h-none text-wrap text-left"
            >
              {currentSuggestion.text}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(ContextSuggestion);
